import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Select } from 'common-src/components/base';
import { getPatientCmsData } from 'common-src/models/Patient';
import {
  addPatientGuideCmsAlignment,
  AssessmentType,
  AssessmentTypeOptions,
  isRccPartnerIdAllowed,
  validatePatientGuideCmsAlignment,
} from 'common-src/models/PatientGuideCmsAlignment';
import { ColorsNew } from 'common-src/styles';
import { singleModelSelector } from 'common-src/utils/selectorUtils';

import useCustomSelector from 'src/hooks/useCustomSelector';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './AddAlignmentPopup.module.scss';
import { AnnualAssessment, InitialAssessment, ReAssessment, Unalignment } from './forms';
import { getInitialState, getRequiredFields } from './helpers';

const AddAlignmentPopup = ({ patientId, assessmentType, assessmentDate, onClose }) => {
  const dispatch = useDispatch();
  const patient = useCustomSelector((state) => singleModelSelector(state, 'Patient', patientId));
  const [data, setData] = useState({ assessmentType, assessmentDate });
  const { errors, setErrors, renderButtons } = usePopup();

  useEffect(() => {
    if (!data.assessmentType || !patient) return;

    setData(getInitialState(data.assessmentType, data.assessmentDate, patient));
  }, [data.assessmentType, data.assessmentDate, JSON.stringify(getPatientCmsData(patient))]);

  const handleInputChange = (field, value) => {
    setData((prevData) => {
      const nextData = { ...prevData, [field]: value };

      if (field === 'residenceType' && !isRccPartnerIdAllowed(value)) {
        nextData.rccPartnerId = null;
      }

      setErrors((prev) => ({
        ...prev,
        ...validatePatientGuideCmsAlignment(field, value, nextData),
        ...(field === 'residenceType'
          ? validatePatientGuideCmsAlignment('rccPartnerId', nextData.rccPartnerId, nextData)
          : {}),
      }));

      return nextData;
    });
  };

  const isFormCompleted = () => {
    if (!data.assessmentType) return false;

    return getRequiredFields(data).every((field) => ![null, undefined, ''].includes(data[field]));
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();

    dispatch(
      addPatientGuideCmsAlignment(
        { patientId, ...data },
        {
          successBlock: () => {
            successCallback('Alignment added!');
            onClose(true);
          },
          errorBlock: (err) => errorCallback(err),
        },
      ),
    );
  };

  const renderForm = () => {
    if (!data.assessmentType) {
      return (
        <Select
          id="assessment-type"
          label="Please select assessment type"
          options={AssessmentTypeOptions}
          onChange={(op) => handleInputChange('assessmentType', op.value)}
          value={AssessmentTypeOptions.find((op) => op.value === data.assessmentType)}
          required
          placeholder="-"
          requiredColor={ColorsNew.mediumDarkRed}
        />
      );
    }

    switch (data.assessmentType) {
      case AssessmentType.InitialAssessment:
        return (
          <InitialAssessment
            data={data}
            handleInputChange={handleInputChange}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case AssessmentType.ReAssessment:
        return (
          <ReAssessment
            data={data}
            handleInputChange={handleInputChange}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case AssessmentType.Unalignment:
        return (
          <Unalignment
            data={data}
            handleInputChange={handleInputChange}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case AssessmentType.AnnualAssessment:
        return (
          <AnnualAssessment
            data={data}
            handleInputChange={handleInputChange}
            errors={errors}
            setErrors={setErrors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <BasePopup id="add-alignment" open title="Add Alignment" onClose={onClose}>
      <div className={styles.wrapper}>{renderForm()}</div>
      {data.assessmentType &&
        renderButtons({
          containerClassName: styles.buttonsContainer,
          onClose,
          onSubmit: onSubmitHandler,
          isSubmitEnabled: isFormCompleted() && !Object.values(errors).some((e) => e),
          submitButtonText: 'Save and Close',
        })}
    </BasePopup>
  );
};

AddAlignmentPopup.propTypes = {
  patientId: PropTypes.number,
  assessmentType: PropTypes.string,
  assessmentDate: PropTypes.string,
  onClose: PropTypes.func,
};

export default AddAlignmentPopup;
